import gql from 'graphql-tag';
import { get as getCookie } from 'js-cookie';
import { compact, castArray, invert } from 'lodash';
import {
  CoreMarketplaceCombinedMarketplaceSearch,
  reverb_search_ListingsSearchRequest_Aggregation as Aggregation,
  core_apimessages_CollectionHeader_CollectionType,
  CoreMarketplaceHeader,
} from '../../schemas/rql';
import { updateParams } from '@reverbdotcom/commons/src/update_params';
import { Location } from 'history';
import { nestedFilterFragment } from '@reverbdotcom/commons/src/components/rql_facets';
import { ListingsCollection } from '@reverbdotcom/commons/src/components/listings_collection';
import { InOtherCartsCardFragment } from '@reverbdotcom/commons/src/components/in_other_carts_card_nudge';
import { Item2WatchBadgeFragment } from '@reverbdotcom/commons/src/components/watch_badge';
import { LEGACY_OFFERS_LISTING_TYPE } from '../shared/constants';
import { IUser } from '@reverbdotcom/commons/src/components/user_context_provider';
import { getOffsetFromPage } from '../../pagination_helpers';
import { ListingCardFragment } from '@reverbdotcom/commons/src/components/listing_card';
import toBool from '../../reverb-js-common/to_boolean';
import { boostedUserCountryCode, bumpedItemRegion } from '@reverbdotcom/commons/src/boosted_user_country_code';
import { bestMatchSignalsExperimentGroup, likelihoodToSellExperimentGroup } from './best_match_signals_experiment_group';
import { MarketplaceSuggestionsFragment } from '../no_results_alert';
import { isExperimentEnabled } from '@reverbdotcom/commons/src/user_context_helpers';
import experiments from '@reverbdotcom/commons/src/experiments';

export interface MarketplaceQueryParams {
  query?: string;
  make?: string[] | string;
  condition?: string[] | string;
  sort?: string;
  year_min?: string;
  year_max?: string;
  price_min?: string;
  price_max?: string;
  on_sale?: boolean;
  holiday_sale?: boolean;
  handmade?: boolean;
  free_shipping?: boolean;
  free_expedited_shipping?: boolean;
  listing_type?: 'offers'; // deprecated
  accepts_offers?: boolean;
  accepts_gift_cards?: boolean;
  preferred_seller?: boolean;
  zero_percent_financing?: boolean;
  ships_to?: string;
  item_region?: string;
  trait_values?: string[] | string; // UUIDs
  product_type?: string;
  category?: string; // slug
  shop?: string; // path only?
  curated_set_slug?: string; // path only
  region?: string; // locale path prefix
  page?: string;
  decade?: string; // used?
  decades?: string[];
  featured_id?: string;
  currency?: string;
  show_only_sold?: boolean;
  showsold?: boolean;
  item_city?: string;
  item_state?: string;
  csp_slug?: string;
  combined_shipping?: boolean;
  accepts_affirm?: boolean;
  proximity?: boolean;
  distance?: string;
  postal_code?: string;
  local_pickup?: boolean;
  min_sale_discount_percent?: string;
}

export interface IPathParams {
  product_type?: string;
  category?: string;
  region?: string;
  brand_slug?: string;
  shop?: string;
  curated_set_slug?: string;
  sale_slug?: string;
}

export interface PageSettings {
  collectionSlug?: string;
  collectionType?: core_apimessages_CollectionHeader_CollectionType;
  showSold?: boolean; // show sold listings even without query param
  holidaySale?: boolean;
  useExperimentalQuery?: boolean;
  useExperimentalRecall?: boolean;
  useExperimentalBestMatchSignals?: boolean;
}

export function updateCategoryAsParams(location: Location, newParams: PlainObject) {
  const { category_slugs, ...queryParams } = newParams;

  const slugs = [].concat(category_slugs).filter(s => typeof s !== 'undefined');

  if (typeof category_slugs !== 'undefined') {
    queryParams.product_type = slugs[0];
    queryParams.category = slugs[1];
  }

  return {
    pathname: location.pathname,
    query: updateParams(location.query, queryParams),
  };
}

const URL_TO_RQL_PARAM = {
  sort: 'sort_slug',
  make: 'brand_slugs',
  condition: 'condition_slugs',
  zero_percent_financing: 'accepts_payment_plans',
  ships_to: 'shipping_region_codes',
};

const RQL_TO_URL_PARAM = invert(URL_TO_RQL_PARAM);

export function renameRqlParam(paramName: string): string {
  const newParam = RQL_TO_URL_PARAM[paramName] || paramName;

  return newParam;
}

export function includePromotedRows(pathParams: IPathParams, queryParams: MarketplaceQueryParams) {
  return !queryParams.show_only_sold && !pathParams.shop;
}

function getProximityParams(queryParams) {
  const proximity = toBool(queryParams.proximity);

  return {
    proximity,
    distance: queryParams.distance,
    postalCode: queryParams.postal_code,
  };
}

function getDefaultSellerLocation(user: Partial<IUser>, queryParams: { item_region?: string }): string {
  if (queryParams.item_region) {
    return queryParams.item_region;
  }

  if (user.shippingRegionCode === 'GB' && !isExperimentEnabled(user, experiments.BOOST_MARKETPLACE_SELLER_LOCATION_V3)) {
    const itemRegionOverride = getCookie('itemRegionOverride');
    return itemRegionOverride || user.shippingRegionCode;
  }

  return undefined;
}

const LIMIT = 45;

function listingsLimit(adTilesCount = 0): number {
  return LIMIT - adTilesCount;
}

export function mapUrlParams(
  pathParams: IPathParams,
  queryParams: MarketplaceQueryParams,
  user: IUser,
  pageSettings: PageSettings,
  adTilesCount = 0,
  listingLimitOverride = undefined,
  queryOverride = '',
): CoreMarketplaceCombinedMarketplaceSearch.Variables {
  let aggs = [
    Aggregation.CATEGORY_SLUGS,
    Aggregation.BRAND_SLUGS,
    Aggregation.CONDITION_SLUGS,
    Aggregation.DECADES,
    Aggregation.CURATED_SETS,
    Aggregation.TRAITS,
  ];
  if (pathParams.brand_slug) {
    aggs = aggs.filter(f => f !== Aggregation.BRAND_SLUGS);
  }
  const brandSlugs = pathParams.brand_slug ? [pathParams.brand_slug] : compact(castArray(queryParams.make));

  const shouldntLoadBumps = [
    pathParams.shop,
    queryParams.show_only_sold,
    queryParams.showsold,
    pageSettings.showSold,
    pageSettings.holidaySale,
    queryOverride,
  ].some(Boolean);

  return {
    aggs,
    brandSlugs,
    shouldntLoadBumps,
    shouldntLoadSuggestions: !isExperimentEnabled(user, experiments.SEARCH_RECALL_IMPROVEMENTS),
    query: queryOverride || queryParams.query,
    priceMin: queryParams.price_min,
    priceMax: queryParams.price_max,
    decades: queryParams.decades,
    yearMin: queryParams.year_min,
    yearMax: queryParams.year_max,
    sortSlug: queryParams.sort,
    limit: listingLimitOverride || listingsLimit(adTilesCount),
    offset: getOffsetFromPage(queryParams.page, listingsLimit(adTilesCount)),
    categorySlugs: compact([
      queryParams.product_type,
      queryParams.category,
    ]),
    conditionSlugs: compact(castArray(queryParams.condition)),
    onSale: queryParams.on_sale,
    holidaySale: pageSettings.holidaySale,
    handmade: queryParams.handmade,
    freeShipping: queryParams.free_shipping,
    freeExpeditedShipping: queryParams.free_expedited_shipping,
    acceptsOffers:
      queryParams.listing_type === LEGACY_OFFERS_LISTING_TYPE ||
      queryParams.accepts_offers,
    acceptsGiftCards: queryParams.accepts_gift_cards,
    preferredSeller: queryParams.preferred_seller,
    acceptsPaymentPlans: queryParams.zero_percent_financing,
    itemRegion: !pathParams.shop ? getDefaultSellerLocation(user, queryParams) : undefined,
    bumpedItemRegion: queryParams.item_region || bumpedItemRegion(user),
    currency: queryParams.currency,
    shippingRegionCode: compact(castArray(queryParams.ships_to)),
    showOnlySold: queryParams.show_only_sold,
    showSold: queryParams.showsold || pageSettings.showSold,
    traitValues: compact(castArray(queryParams.trait_values)),
    itemState: compact(castArray(queryParams.item_state)),
    itemCity: compact(castArray(queryParams.item_city)),
    shopSlug: pathParams.shop,
    curatedSetSlugs: compact(castArray(pathParams.curated_set_slug)),
    saleSlugs: compact(castArray(pathParams.sale_slug)),
    cspSlug: queryParams.csp_slug,
    combinedShipping: queryParams.combined_shipping,
    acceptsAffirm: queryParams.accepts_affirm,
    terminateEarly: queryParams.show_only_sold && pathParams.shop === undefined,
    fallbackToOr: true,
    withProximityFilter: getProximityParams(queryParams),
    localPickup: queryParams.local_pickup,
    minSaleDiscountPercent: queryParams.min_sale_discount_percent,
    useExperimentalQuery: pageSettings.useExperimentalQuery,
    boostedItemRegionCode: boostedUserCountryCode(user),
    useExperimentalRegionBoost: isExperimentEnabled(user, experiments.BOOST_MARKETPLACE_SELLER_LOCATION_V3),
    bestMatchSignalsV2ExperimentGroup: bestMatchSignalsExperimentGroup(user, pageSettings),
    likelihoodToSellExperimentGroup: likelihoodToSellExperimentGroup(user, pageSettings),
    useExperimentalRecall: pageSettings.useExperimentalRecall,
  };
}

export function mapUrlParamsForHeader(
  pathParams: IPathParams,
  queryParams: MarketplaceQueryParams,
  pageSettings: PageSettings,
): CoreMarketplaceHeader.Variables {
  const brandSlugs = pathParams.brand_slug ? [pathParams.brand_slug] : compact(castArray(queryParams.make));
  const { collectionSlug, collectionType } = pageSettings;

  return {
    collectionType,
    collectionSlug,
    skipCollectionHeader: !collectionSlug,
    brandSlugs,
    categorySlugs: compact([
      queryParams.product_type,
      queryParams.category,
    ]),
    query: queryParams.query,
    yearMin: queryParams.year_min,
    yearMax: queryParams.year_max,
    conditionSlugs: compact(castArray(queryParams.condition)),
    shopSlug: pathParams.shop,
    curatedSetSlug: pathParams.curated_set_slug || pathParams.sale_slug,
  };
}

// NOTE:
// in order make sure all searches return a similar result set,
// please pass relevant inputs to ALL searches defined below!
export const combinedMarketplaceSearchQuery = gql`
  query Core_Marketplace_CombinedMarketplaceSearch(
    $query: String
    $currency: String
    $priceMax: String
    $priceMin: String
    $decades: [String]
    $yearMax: String
    $yearMin: String
    $sortSlug: String
    $limit: Int
    $offset: Int
    $categorySlugs: [String]
    $brandSlugs: [String]
    $aggs: [reverb_search_ListingsSearchRequest_Aggregation]
    $conditionSlugs: [String]
    $onSale: Boolean
    $holidaySale: Boolean
    $handmade: Boolean
    $freeShipping: Boolean
    $freeExpeditedShipping: Boolean
    $acceptsOffers: Boolean
    $acceptsGiftCards: Boolean
    $preferredSeller: Boolean
    $acceptsPaymentPlans: Boolean
    $itemRegion: String
    $bumpedItemRegion: String
    $shippingRegionCode: [String]
    $showOnlySold: Boolean
    $showSold: Boolean
    $traitValues: [String]
    $itemState: [String]
    $itemCity: [String]
    $shopSlug: String
    $curatedSetSlugs: [String]
    $saleSlugs: [String]
    $cspSlug: String
    $boostByBumpRate: Boolean
    $bumpCount: Int
    $bumpOffset: Int
    $bumpLimit: Int
    $sort: reverb_search_ListingsSearchRequest_Sort
    $combinedShipping: Boolean
    $acceptsAffirm: Boolean
    $terminateEarly: Boolean
    $fallbackToOr: Boolean
    $shouldntLoadBumps: Boolean!
    $withProximityFilter: Input_reverb_search_ProximityFilterRequest
    $localPickup: Boolean
    $minSaleDiscountPercent: String
    $useExperimentalQuery: Boolean
    $boostedItemRegionCode: String
    $useExperimentalRegionBoost: Boolean
    $bestMatchSignalsV2ExperimentGroup: Int
    $likelihoodToSellExperimentGroup: Int
    $useExperimentalRecall: Boolean
    $shouldntLoadSuggestions: Boolean!
  ) {
    bumpedSearch: listingsSearch(input: {
      query: $query
      currency: $currency
      priceMax: $priceMax
      priceMin: $priceMin
      decades: $decades
      yearMax: $yearMax
      yearMin: $yearMin
      sortSlug: $sortSlug
      traitValues: $traitValues
      categorySlugs: $categorySlugs
      brandSlugs: $brandSlugs
      conditionSlugs: $conditionSlugs
      onSale: $onSale
      handmade: $handmade
      freeShipping: $freeShipping
      freeExpeditedShipping: $freeExpeditedShipping
      acceptsOffers: $acceptsOffers
      acceptsGiftCards: $acceptsGiftCards
      preferredSeller: $preferredSeller
      acceptsPaymentPlans: $acceptsPaymentPlans
      itemRegion: $bumpedItemRegion
      shippingRegionCodes: $shippingRegionCode
      bumpCount: $bumpCount
      showOnlySold: $showOnlySold
      showSold: $showSold
      itemState: $itemState
      itemCity: $itemCity
      shopSlug: $shopSlug
      curatedSetSlugs: $curatedSetSlugs
      saleSlugs: $saleSlugs
      sort: $sort
      bumpedOnly: true
      limit: $bumpLimit
      offset: $bumpOffset
      cspSlug: $cspSlug
      boostByBumpRate: $boostByBumpRate
      combinedShipping: $combinedShipping
      acceptsAffirm: $acceptsAffirm
      withProximityFilter: $withProximityFilter
      localPickup: $localPickup
      minSaleDiscountPercent: $minSaleDiscountPercent
      useExperimentalQuery: $useExperimentalQuery
      boostedItemRegionCode: $boostedItemRegionCode
      useExperimentalRegionBoost: $useExperimentalRegionBoost
      bestMatchSignalsV2ExperimentGroup: $bestMatchSignalsV2ExperimentGroup
      useExperimentalRecall: $useExperimentalRecall
    }) @skip(if: $shouldntLoadBumps) {
      listings {
        _id
        ...ListingCardFields
        ... Item2WatchBadgeData
        ...BumpKey
        ...ShopFields
      }
    }
    aggsSearch: listingsSearch(
      input: {
        query: $query
        currency: $currency
        priceMax: $priceMax
        priceMin: $priceMin
        decades: $decades
        yearMax: $yearMax
        yearMin: $yearMin
        sortSlug: $sortSlug
        traitValues: $traitValues
        limit: 0,
        categorySlugs: $categorySlugs
        brandSlugs: $brandSlugs
        withAggregations: $aggs
        conditionSlugs: $conditionSlugs
        onSale: $onSale
        holidaySale: $holidaySale
        handmade: $handmade
        freeShipping: $freeShipping
        freeExpeditedShipping: $freeExpeditedShipping
        acceptsOffers: $acceptsOffers
        acceptsGiftCards: $acceptsGiftCards
        preferredSeller: $preferredSeller
        acceptsPaymentPlans: $acceptsPaymentPlans
        itemRegion: $itemRegion
        shippingRegionCodes: $shippingRegionCode
        showOnlySold: $showOnlySold
        showSold: $showSold
        itemState: $itemState
        itemCity: $itemCity
        shopSlug: $shopSlug
        curatedSetSlugs: $curatedSetSlugs
        saleSlugs: $saleSlugs
        cspSlug: $cspSlug
        combinedShipping: $combinedShipping
        acceptsAffirm: $acceptsAffirm
        terminateEarly: $terminateEarly
        fallbackToOr: $fallbackToOr
        withProximityFilter: $withProximityFilter
        localPickup: $localPickup
        minSaleDiscountPercent: $minSaleDiscountPercent
        useExperimentalQuery: $useExperimentalQuery
        boostedItemRegionCode: $boostedItemRegionCode
        useExperimentalRegionBoost: $useExperimentalRegionBoost
        bestMatchSignalsV2ExperimentGroup: $bestMatchSignalsV2ExperimentGroup
        likelihoodToSellExperimentGroup: $likelihoodToSellExperimentGroup
        useExperimentalRecall: $useExperimentalRecall
      }
    ) {
      filters {
        ... NestedFilter
      }
    }
    listingsSearch(
      input: {
        query: $query
        currency: $currency
        priceMax: $priceMax
        priceMin: $priceMin
        decades: $decades
        yearMax: $yearMax
        yearMin: $yearMin
        sortSlug: $sortSlug
        traitValues: $traitValues
        limit: $limit
        offset: $offset
        categorySlugs: $categorySlugs
        brandSlugs: $brandSlugs
        conditionSlugs: $conditionSlugs
        onSale: $onSale
        holidaySale: $holidaySale
        handmade: $handmade
        freeShipping: $freeShipping
        freeExpeditedShipping: $freeExpeditedShipping
        acceptsOffers: $acceptsOffers
        acceptsGiftCards: $acceptsGiftCards
        preferredSeller: $preferredSeller
        acceptsPaymentPlans: $acceptsPaymentPlans
        itemRegion: $itemRegion
        shippingRegionCodes: $shippingRegionCode
        showOnlySold: $showOnlySold
        showSold: $showSold
        itemState: $itemState
        itemCity: $itemCity
        shopSlug: $shopSlug
        curatedSetSlugs: $curatedSetSlugs
        saleSlugs: $saleSlugs
        cspSlug: $cspSlug
        sort: $sort
        combinedShipping: $combinedShipping
        acceptsAffirm: $acceptsAffirm
        terminateEarly: $terminateEarly
        fallbackToOr: $fallbackToOr
        withProximityFilter: $withProximityFilter
        localPickup: $localPickup
        minSaleDiscountPercent: $minSaleDiscountPercent
        useExperimentalQuery: $useExperimentalQuery
        boostedItemRegionCode: $boostedItemRegionCode
        bestMatchSignalsV2ExperimentGroup: $bestMatchSignalsV2ExperimentGroup
        likelihoodToSellExperimentGroup: $likelihoodToSellExperimentGroup
        useExperimentalRegionBoost: $useExperimentalRegionBoost
        useExperimentalRecall: $useExperimentalRecall
      }
    ) {
      total
      offset
      limit
      suggestedQueries
      listings {
        _id
        ... ListingCardFields
        ... Item2WatchBadgeData
        ... InOtherCartsCardData
        ... ShopFields
      }
      fallbackListings {
        _id
        ... ListingCardFields
        ... InOtherCartsCardData
        ... Item2WatchBadgeData
      }
      suggestions @skip(if: $shouldntLoadSuggestions) {
        ... MarketplaceSuggestions
      }
    }
  }
  ${ListingCardFragment}
  ${Item2WatchBadgeFragment}
  ${ListingsCollection.fragments.bumpKey}
  ${ListingsCollection.fragments.shopFields}
  ${InOtherCartsCardFragment}
  ${nestedFilterFragment}
  ${MarketplaceSuggestionsFragment}
`;
